import { useCallback, useSyncExternalStore } from 'react';

/**
 * Which city the app is showing: `cg.city` (contract §3). A tiny store rather
 * than React state so every hook keyed by city reads the same value. Session 4
 * builds the chooser and the header pill on top of it.
 */

export type CityKey = 'nyc' | 'boston';

export const CITIES: { key: CityKey; label: string; short: string; enabled: boolean }[] = [
  { key: 'nyc', label: 'New York City', short: 'NYC', enabled: true },
  { key: 'boston', label: 'Boston', short: 'Boston', enabled: false },
];

export const CITY_KEY = 'cg.city';
const DEFAULT_CITY: CityKey = 'nyc';

const listeners = new Set<() => void>();

function read(): CityKey | null {
  try {
    const value = localStorage.getItem(CITY_KEY);
    return CITIES.some((c) => c.key === value) ? (value as CityKey) : null;
  } catch {
    return null;
  }
}

/** Held here once read, so a blocked localStorage still switches for the session. */
let current: CityKey | null = read();

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The stored choice, or null on a first visit (Session 4 shows the chooser then). */
export const storedCity = (): CityKey | null => current;

export function setCity(city: CityKey): void {
  current = city;
  try {
    localStorage.setItem(CITY_KEY, city);
  } catch {
    // Site data blocked: the choice lasts this session.
  }
  listeners.forEach((l) => l());
}

/**
 * A change of city from the pill or the chooser. Home is per city and means
 * nothing in another one, so the old city's is cleared (contract §4); RSVPs and
 * hidden events stay under their own city's keys. The caller drops any distance
 * filter; roles and topics carry over.
 */
export function switchCity(to: CityKey): void {
  const from = current;
  if (from && from !== to) {
    try {
      localStorage.removeItem(`cg.home.${from}`);
    } catch {
      // Site data blocked: nothing was stored.
    }
  }
  setCity(to);
}

export function useCity(): { city: CityKey; chosen: boolean; setCity: (city: CityKey) => void } {
  const stored = useSyncExternalStore(subscribe, storedCity, storedCity);
  const choose = useCallback((city: CityKey) => setCity(city), []);
  return { city: stored ?? DEFAULT_CITY, chosen: stored !== null, setCity: choose };
}
